import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface LocalizedAttributeContent {
  [language: string]: {
    name: string
  }
}

export interface FoodAttribute {
  id: number | string
  localizedContent: LocalizedAttributeContent
}

export interface FoodAttributesData {
  properties: FoodAttribute[]
  allergens: FoodAttribute[]
  additives: FoodAttribute[]
}

export interface FoodAttributesResponse {
  error: boolean
  message: string
  data: FoodAttributesData
}

export const useFoodAttributes = (getRestaurantId?: () => string) => {
  const properties = ref<FoodAttribute[]>([])
  const allergens = ref<FoodAttribute[]>([])
  const additives = ref<FoodAttribute[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch food attributes (properties, allergens, additives) from backend
   */
  const fetchFoodAttributes = async (): Promise<void> => {
    if (!getRestaurantId) {
      console.warn('Restaurant ID getter not provided')
      return
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to fetch food attributes'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.get<FoodAttributesResponse>(
        `/restaurant/${restaurantId}/food-attributes`
      )

      if (response.data.error === false && response.data.data) {
        properties.value = response.data.data.properties || []
        allergens.value = response.data.data.allergens || []
        additives.value = response.data.data.additives || []
      } else {
        error.value = response.data.message || 'Failed to fetch food attributes'
        properties.value = []
        allergens.value = []
        additives.value = []
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to fetch food attributes'
      properties.value = []
      allergens.value = []
      additives.value = []
      console.error('Error fetching food attributes:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Reset the module state
   */
  const reset = () => {
    properties.value = []
    allergens.value = []
    additives.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    properties,
    allergens,
    additives,
    isLoading,
    error,
    fetchFoodAttributes,
    reset,
  }
}
